import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useMemo } from 'react';

import { useScrollingArrows } from '@agilemd/ui-button-legacy';
import { addVariants } from '@agilemd/ui-utils';

import TabItem, { VARIANTS as ITEM_VARIANTS } from '../Item/index.js';
import Pane, { VARIANTS as PANE_VARIANTS } from '../Pane/index.ts';
import ScrollContext from './ScrollContext.ts';
import styles from './styles.module.scss';

export const COLORS = ['white', 'light'];
export const DIRECTIONS = ['horizontal', 'vertical'];
export const VARIANTS = COLORS.concat(DIRECTIONS).concat([
  'default',
  'no-border',
  'border',
  'centered'
]);
const List = ({
  children,
  variant = 'horizontal',
  color = 'white',
  className = '',
  labels = [],
  shouldRenderAllPanes = true
}) => {
  const directionVariant = useMemo(
    () => [].concat(variant).find(v => DIRECTIONS.includes(v)) || 'horizontal',
    [variant]
  );

  const {
    offset,
    setOffset,
    canOverscroll,
    outsideContainerRef,
    insideContainerRef,
    rightArrow,
    leftArrow
  } = useScrollingArrows();
  const context = useMemo(
    () => ({
      offset,
      setOffset,
      canOverscroll
    }),
    [offset, setOffset, canOverscroll]
  );
  return (
    <ScrollContext.Provider value={context}>
      <div
        className={addVariants('aglmd-tabs__container', styles, variant)}
        ref={outsideContainerRef}
      >
        {leftArrow}
        <ul
          className={classnames(
            addVariants('aglmd-tabs__list', styles, variant, color, directionVariant),
            className
          )}
          ref={insideContainerRef}
        >
          {labels.length
            ? labels.map((label, index) => (
                <TabItem
                  key={label}
                  value={index}
                  variant={[]
                    .concat(variant)
                    .concat(directionVariant)
                    .filter(singleVariant => ITEM_VARIANTS.includes(singleVariant))}
                  color={color}
                >
                  {label}
                </TabItem>
              ))
            : children}
        </ul>
        {Boolean(labels.length) &&
          children.map((child, index) => (
            <Pane
              key={String(index)}
              value={index}
              variant={[]
                .concat(variant)
                .filter(singleVariant => PANE_VARIANTS.includes(singleVariant))}
              shouldRenderContent={shouldRenderAllPanes}
            >
              {child}
            </Pane>
          ))}
        {rightArrow}
      </div>
    </ScrollContext.Provider>
  );
};

List.propTypes = {
  /**
   * Each child to be rendered as a tab pane
   */
  children: PropTypes.arrayOf(PropTypes.node).isRequired,
  /**
   * Variant to use
   */
  variant: PropTypes.oneOfType([
    PropTypes.oneOf(VARIANTS),
    PropTypes.bool,
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.oneOf(VARIANTS), PropTypes.bool]))
  ]),
  /**
   * The color of the TabList
   */
  color: PropTypes.oneOf(COLORS),
  /**
   * Class to apply
   */
  className: PropTypes.string,
  /**
   * The labels to make Tab Items
   */
  labels: PropTypes.arrayOf(PropTypes.string),
  /**
   * Whether to render all panes or hide with just CSS (perf enhancement)
   */
  shouldRenderAllPanes: PropTypes.bool
};

export default List;
